/*
A full-screen scatter of confetti, for the moment a buyer claims their vouchers.

Two populations, fired together, because one alone does not fill a screen:

  - a BURST out of the button that was pressed, so the celebration is visibly caused by the click;
  - a SHOWER seeded across the entire width above the top edge, staggered in height so it keeps
    falling past the viewport for a few seconds rather than arriving as one curtain.

Between them every part of the screen gets confetti. Counts scale with the viewport area, so a wide
desktop is covered as thoroughly as a phone instead of looking sparse.

Self-contained on purpose - no dependency, nothing to install, no bundle added. It paints onto a
single fixed, pointer-events:none canvas layered over the page, runs one requestAnimationFrame loop
while particles are alive, then removes the canvas and the loop entirely. Calling it again while a
scatter is in flight adds to the same canvas rather than stacking new ones.

Deliberately unobtrusive despite the size: it cannot be clicked, it never blocks the dialog
underneath, and it stops by itself. If the tab is hidden or the browser has no canvas the call is a
silent no-op, so a caller never has to guard it.
*/

// Above MUI's dialog (1300) so the burst is visible over the voucher modal, below nothing that
// matters - the canvas ignores pointer events, so it cannot swallow a click on what it covers.
const CANVAS_Z_INDEX = 2000;
const GRAVITY = 0.22;
const DRAG = 0.992;
const FADE_FROM = 0.72; // fraction of life after which a particle starts fading out

// One particle per this many square pixels of viewport, per population. Tuned so a 1440x900 desktop
// gets roughly 260 shower pieces and 130 burst pieces - dense enough to read as "the whole screen",
// cheap enough that the frame loop is a few hundred fillRects.
const SHOWER_AREA_PER_PARTICLE = 5000;
const BURST_AREA_PER_PARTICLE = 10000;
// Bounds so a phone still gets a real scatter and a very large monitor cannot run away with it.
const SHOWER_MIN = 120;
const SHOWER_MAX = 420;
const BURST_MIN = 70;
const BURST_MAX = 200;

// Medimny blue first - the burst should read as the app celebrating, not as generic party colours.
const COLORS = ['#072791', '#3f6fd8', '#0f7a4e', '#f5b301', '#e0457b', '#ffffff'];

let canvas = null;
let ctx = null;
let particles = [];
let frame = null;

const removeCanvas = () => {
  if (frame) {
    window.cancelAnimationFrame(frame);
    frame = null;
  }
  if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
  canvas = null;
  ctx = null;
  particles = [];
};

const ensureCanvas = () => {
  if (canvas) return true;
  canvas = document.createElement('canvas');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  canvas.setAttribute('aria-hidden', 'true');
  Object.assign(canvas.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: String(CANVAS_Z_INDEX),
  });
  ctx = canvas.getContext ? canvas.getContext('2d') : null;
  if (!ctx) {
    canvas = null;
    return false;
  }
  document.body.appendChild(canvas);
  return true;
};

const baseParticle = () => ({
  size: 6 + Math.random() * 7,
  color: COLORS[Math.floor(Math.random() * COLORS.length)],
  rotation: Math.random() * Math.PI,
  spin: (Math.random() - 0.5) * 0.3,
  // A slow side-to-side drift on top of the velocity, so pieces flutter like paper instead of
  // dropping on rails. Amplitude is per-particle, which is what keeps the field from moving as one.
  swayAmp: 0.4 + Math.random() * 1.4,
  swayFreq: 0.03 + Math.random() * 0.05,
  swayPhase: Math.random() * Math.PI * 2,
  life: 0,
});

const makeBurstParticle = (originX, originY) => {
  // Fired upward and outward in a wide fan, hard enough to carry across a desktop viewport rather
  // than piling up around the button. Gravity brings them back down through the page.
  const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.6;
  const speed = 9 + Math.random() * 15;
  return {
    ...baseParticle(),
    x: originX,
    y: originY,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    maxLife: 150 + Math.floor(Math.random() * 90),
  };
};

const makeShowerParticle = (width, height) => {
  // Seeded across the FULL width, and staggered up to two screen-heights above the top edge: the
  // ones started highest arrive last, which is what turns a single curtain into a few seconds of
  // falling confetti. They are culled the moment they pass the bottom.
  return {
    ...baseParticle(),
    x: Math.random() * width,
    y: -Math.random() * height * 2,
    vx: (Math.random() - 0.5) * 3,
    vy: 2 + Math.random() * 4,
    maxLife: 260 + Math.floor(Math.random() * 160),
  };
};

const step = () => {
  if (!ctx || !canvas) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  particles = particles.filter((p) => {
    p.life += 1;
    p.vy += GRAVITY;
    p.vx *= DRAG;
    p.vy *= DRAG;
    p.x += p.vx + Math.sin(p.life * p.swayFreq + p.swayPhase) * p.swayAmp;
    p.y += p.vy;
    p.rotation += p.spin;
    // Retired once it has run its life or fallen off the bottom - whichever comes first.
    if (p.life > p.maxLife || p.y > canvas.height + p.size) return false;
    // Shower pieces start above the top edge and are still on their way in. Alive, but nothing to
    // paint yet - and skipping the draw keeps the early frames cheap while the field is densest.
    if (p.y < -p.size) return true;

    const fadeStart = p.maxLife * FADE_FROM;
    ctx.globalAlpha =
      p.life > fadeStart ? Math.max(0, 1 - (p.life - fadeStart) / (p.maxLife - fadeStart)) : 1;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rotation);
    ctx.fillStyle = p.color;
    // Rectangles, not circles - a flat spinning strip reads as paper rather than as bubbles.
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
    ctx.restore();
    return true;
  });

  ctx.globalAlpha = 1;
  if (particles.length > 0) {
    frame = window.requestAnimationFrame(step);
  } else {
    removeCanvas();
  }
};

/*
Scatter confetti across the whole screen.

`origin` is an optional {x, y} in viewport pixels - pass the clicked element's centre so the burst
half appears to come out of the button the buyer pressed. Defaults to the middle of the viewport, a
bit above centre, which is where a dialog's action row sits. The shower half covers the full width
regardless of the origin.
*/
export const scatterConfetti = (origin) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!window.requestAnimationFrame || !document.body) return;
  // Nothing to celebrate at into a hidden tab; the frame loop would be throttled anyway.
  if (document.hidden) return;
  // Respect a buyer who has asked their OS for less motion - they still get the claim, silently.
  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;
  if (!ensureCanvas()) return;

  // Re-sized on every scatter: the window may have changed since the canvas was created, and a
  // stale size would seed the shower over the wrong width.
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const width = canvas.width;
  const height = canvas.height;
  const area = width * height;
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const x = origin && Number.isFinite(origin.x) ? origin.x : width / 2;
  const y = origin && Number.isFinite(origin.y) ? origin.y : height * 0.45;

  const showerCount = clamp(Math.round(area / SHOWER_AREA_PER_PARTICLE), SHOWER_MIN, SHOWER_MAX);
  const burstCount = clamp(Math.round(area / BURST_AREA_PER_PARTICLE), BURST_MIN, BURST_MAX);

  for (let i = 0; i < showerCount; i++) particles.push(makeShowerParticle(width, height));
  for (let i = 0; i < burstCount; i++) particles.push(makeBurstParticle(x, y));
  if (!frame) frame = window.requestAnimationFrame(step);
};

/*
The centre of the element a click came from, for `scatterConfetti(origin)`. Returns undefined when
the event carries no element, so it can be passed straight through.
*/
export const originFromEvent = (event) => {
  const target = event && event.currentTarget;
  if (!target || !target.getBoundingClientRect) return undefined;
  const rect = target.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

export default scatterConfetti;
